import { CommonModule, formatNumber } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { ClarityModule } from '@clr/angular';
import { Column, Row } from 'src/app/models/ui/table';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { FkColorsState } from 'src/app/services/fk-colors.state';
import { TextBoolDgCell } from './text-bool-dg-cell';
import { ObjTypePipe } from 'src/app/pipes/objtype.pipe';

@Component({
  selector: 'app-icon-dg-cell',
  template: `
    @let config = cellConfig();
    @if (config.condition !== false) {
      @let size = config.size ?? 26;
      <div class="h-100 w-100-p d-f clr-align-items-center">
        @switch (config.type) {
          @case ('text') {
            <app-dg-cell [data]="config.value" [col]="column().id" />
          }
          @case ('icon') {
            <span>
              <cds-icon
                class="grid-icon"
                [title]="config.title ?? ''"
                [size]="size.toString()"
                [shape]="config.shape ?? 'circle'"
                [solid]="config.solid"
                [style.color]="config.color"
                [class]="config.class" />
              @if (config.value !== undefined) {
                &nbsp;{{ config.value }}
              }
            </span>
          }
          @case ('tooltip') {
            <clr-tooltip>
              <cds-icon
                clrTooltipTrigger
                class="grid-icon"
                [size]="size.toString()"
                [shape]="config.shape"
                [solid]="config.solid"
                [style.color]="config.color"
                [class]="config.class" />
              <clr-tooltip-content [clrPosition]="'bottom-right'" [clrSize]="'md'">
                {{ config.tooltipText | mrTranslate }}
              </clr-tooltip-content>
            </clr-tooltip>
          }
        }
      </div>
    }
  `,
  host: {
    'data-testid': 'app-icon-dg-cell',
  },
  imports: [CommonModule, ClarityModule, MrTranslatePipe, TextBoolDgCell],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconDgCellComponent {
  private readonly fkColors = inject(FkColorsState);

  protected readonly row = input.required<Row>();
  protected readonly column = input.required<Column>();

  protected readonly cellConfig = computed((): Config => {
    const col = this.column().id;
    const r = this.row();

    switch (col.toLowerCase()) {
      case 'kav':
        return {
          type: 'icon',
          condition: ObjTypePipe.isPoint(r.typ_id as number) && r.KAV != null,
          color: r.kavcolor as string,
          solid: true,
          value: formatNumber(r.KAV as number, 'de', '.0-3'),
        };
      case 'qi':
        return {
          type: 'icon',
          condition: ObjTypePipe.isPoint(r.typ_id as number) && r.KAV != null,
          color: r.qicolor as string,
          solid: true,
          value: r.QI,
        };

      case 'minfk':
        return {
          type: 'icon',
          color: this.fkColors.fromFK(+this.row().MinFK),
          solid: true,
        };

      case 'typ':
        return {
          type: 'icon',
          class: 'fill-black',
          shape: r.typ_icon ? `objekttyp${r.typ_icon}` : 'unknown-status',
          title: r.typ as string,
        };
      case 'rep':
        return {
          type: 'tooltip',
          color: r.Rep ? 'red' : 'green',
          shape: r.Rep ? 'shield-x' : 'shield-check',
          tooltipText: r.Rep ? `Objekt ist noch im Replikat: ${r.Rep}` : 'Objekt ist freigegeben',
        };
      case 'sperrstatus':
        return {
          type: 'icon',
          class: 'fill-black',
          shape: +r.sperrstatus > 0 ? 'gesperrt' : 'blank',
        };
      case 'sp': {
        const otypid = r.OTYPID ?? r.otypid;
        const richtung = otypid == 1 ? r.Richtung || r.SP : 99;
        return {
          type: 'icon',
          class: 'fill-black',
          shape: +r.SP > 0 ? `richtung${richtung}` : 'blank',
        };
      }
      case 'lf':
      case 'lfstatus': {
        const lf = r.LF ?? r.lfstatus;
        return {
          type: 'icon',
          class: 'fill-black',
          shape: +lf > 0 ? `lastelle${+lf / 10}`.replace('.', '_') : 'blank',
        };
      }

      case 'st':
        return {
          type: 'icon',
          class: 'fill-inverse',
          shape: r.ST == 1 ? 'exclamation-triangle' : 'blank',
        };
      case 'si':
        return {
          type: 'icon',
          class: 'invalid-red',
          shape: r.SI == 1 ? 'warning-standard' : 'blank',
        };
      default:
        return {
          type: 'text',
          value: r[col],
        };
    }
  });
}

interface BaseConfig {
  type?: 'text';
  value?: unknown;
  class?: string;
  size?: number;
  title?: string;
  condition?: boolean;
}

interface IconConfig extends Omit<BaseConfig, 'type'> {
  type: 'icon';
  shape?: string;
  solid?: true;
  color?: string;
}

interface TooltipConfig extends Omit<IconConfig, 'type'> {
  type: 'tooltip';
  tooltipText: string;
}

type Config = BaseConfig | IconConfig | TooltipConfig;
